import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO configuration for each route
const seoConfig = {
  '/': {
    title: 'Aurous Consultancy | AI-Powered IT Teams & Delivery Partner Malaysia',
    description: 'Aurous Consultancy provides AI-ready engineers, managed delivery teams, and compliance-driven technology services. Malaysia Digital certified IT consultancy.',
    keywords: 'AI consulting Malaysia, IT consultancy Cyberjaya, AI engineers, managed teams, software development',
    ogType: 'website'
  },
  '/ai-talent': {
    title: 'AI Talent Pool | Pre-Vetted AI Engineers & Data Scientists | Aurous',
    description: 'Access our network of pre-vetted AI engineers, data specialists, and ML experts ready to accelerate your projects. Browse available talent or submit your profile.',
    keywords: 'AI engineers, data scientists, ML experts, hire AI talent, contract engineers Malaysia',
    ogType: 'website'
  },
  '/join-talent-pool': {
    title: 'Join Our AI Talent Network | Aurous Consultancy',
    description: 'Join our curated AI talent network. Connect with top companies looking for specialized AI expertise. Verified opportunities and competitive rates.',
    keywords: 'AI jobs Malaysia, data scientist jobs, ML engineer opportunities, tech talent network',
    ogType: 'website'
  },
  '/ai-news': {
    title: 'AI News & Insights | Latest in Artificial Intelligence | Aurous',
    description: 'Stay updated with the latest AI news, trends, and insights. Enterprise AI, LLMs, technology breakthroughs, and industry updates.',
    keywords: 'AI news, artificial intelligence trends, LLM updates, enterprise AI, tech news',
    ogType: 'website'
  },
  '/our-work': {
    title: 'Our Work | AI & IT Project Portfolio | Aurous Consultancy',
    description: 'Explore our portfolio of successful AI and technology projects across fintech, healthcare, enterprise, and more industries.',
    keywords: 'IT project portfolio, AI case studies, software development projects, enterprise solutions',
    ogType: 'website'
  },
  '/careers': {
    title: 'Career Opportunities | Join Aurous Consultancy',
    description: 'Join our team of talented professionals. Explore career opportunities in AI, software development, and technology consulting.',
    keywords: 'IT jobs Malaysia, AI career opportunities, software developer jobs Cyberjaya, tech jobs',
    ogType: 'website'
  },
  '/blogs': {
    title: 'Blog | Insights & Resources | Aurous Consultancy',
    description: 'Expert insights, tips, and resources on AI, technology, and digital transformation from our team.',
    keywords: 'tech blog, AI insights, software development tips, digital transformation',
    ogType: 'website'
  },
  '/login': {
    title: 'Login | Aurous Consultancy',
    description: 'Access your Aurous Consultancy account and products.',
    keywords: '',
    ogType: 'website',
    noIndex: true
  }
};

// Default SEO for pages not in config
const defaultSeo = {
  title: 'Aurous Consultancy | AI-Powered IT Teams & Delivery Partner',
  description: 'AI-ready engineers, managed delivery teams, and compliance-driven technology services.',
  keywords: 'AI consulting, IT consultancy, software development, Malaysia',
  ogType: 'website'
};

/**
 * SEO Component - Updates document head with meta tags
 * Usage: <SEOHead /> - Place in App.jsx or individual pages
 */
export function SEOHead({ 
  title, 
  description, 
  keywords, 
  ogImage,
  ogType = 'website',
  noIndex = false,
  canonicalUrl 
}) {
  const location = useLocation();
  
  useEffect(() => {
    // Get SEO config for current path or use defaults
    const pathConfig = seoConfig[location.pathname] || defaultSeo;
    
    // Use props if provided, otherwise use config
    const pageTitle = title || pathConfig.title;
    const pageDescription = description || pathConfig.description;
    const pageKeywords = keywords || pathConfig.keywords;
    const pageOgType = ogType || pathConfig.ogType;
    const shouldNoIndex = noIndex || pathConfig.noIndex;
    
    // Update document title
    document.title = pageTitle;
    
    // Helper function to update or create meta tag
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (content) {
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      } else if (meta) {
        meta.remove();
      }
    };
    
    // Update meta tags
    updateMetaTag('description', pageDescription);
    updateMetaTag('keywords', pageKeywords);
    
    // Update robots meta
    if (shouldNoIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Update Open Graph tags
    updateMetaTag('og:title', pageTitle, true);
    updateMetaTag('og:description', pageDescription, true);
    updateMetaTag('og:type', pageOgType, true);
    updateMetaTag('og:url', `https://aurousconsultancy.com${location.pathname}`, true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }
    
    // Update Twitter Card tags
    updateMetaTag('twitter:title', pageTitle);
    updateMetaTag('twitter:description', pageDescription);
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalHref = canonicalUrl || `https://aurousconsultancy.com${location.pathname}`;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);
    
    // Track page view in GA4
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_path: location.pathname,
        page_location: window.location.href
      });
    }
    
  }, [location.pathname, title, description, keywords, ogImage, ogType, noIndex, canonicalUrl]);
  
  return null; // This component doesn't render anything
}

/**
 * Hook for tracking events
 */
export function useAnalytics() {
  const trackEvent = (eventName, params = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }
    if (window.posthog) {
      window.posthog.capture(eventName, params);
    }
  };
  
  const trackCTAClick = (ctaName, ctaLocation) => {
    trackEvent('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      page_path: window.location.pathname
    });
  };
  
  const trackFormSubmission = (formName, success = true) => {
    trackEvent('form_submission', {
      form_name: formName,
      success: success,
      page_path: window.location.pathname
    });
  };
  
  const trackConversion = (conversionType, value = null) => {
    trackEvent('conversion', {
      conversion_type: conversionType,
      value: value,
      page_path: window.location.pathname
    });
  };
  
  const trackOutboundLink = (url, linkText) => {
    trackEvent('outbound_link', {
      link_url: url,
      link_text: linkText
    });
  };
  
  return {
    trackEvent,
    trackCTAClick,
    trackFormSubmission,
    trackConversion,
    trackOutboundLink
  };
}

/**
 * Structured Data Component for specific pages
 */
export function StructuredData({ type, data }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `structured-data-${type}`;
    
    // Remove existing script if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': type,
      ...data
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(`structured-data-${type}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);
  
  return null;
}

export default SEOHead;
